import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import FlightTakeoffOutlinedIcon from "@mui/icons-material/FlightTakeoffOutlined";
import DirectionsBoatOutlinedIcon from "@mui/icons-material/DirectionsBoatOutlined";
import AltRouteOutlinedIcon from "@mui/icons-material/AltRouteOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material/styles";
import {
  getA3FamiliasTalla,
  getA3FamiliasTallaClasificadas,
  getA3PedidoLineasMatriz,
  getA3Tallas,
  searchA3Articulos,
} from "../../api/Produccion/a3ArticulosApi";
import DocumentosLogistica from "./DocumentosLogistica";
import {
  deleteVinculoPedidoLogisticaEnvio,
  getLogisticaEnvioConsolidacionRef,
  getLogisticaEnvioDetalle,
  updateLogisticaEnvio,
  updateLogisticaEnvioPedidoCabeceras,
  updateLogisticaEnvioLineas,
} from "../../api/Logistica/logisticaEnviosApi";
import { getForwarderProveedores } from "../../api/ProveedoresyFabricas/proveedoresApi";
import {
  getFabricasByProveedor,
  getFabricasNombresByProveedorNombre,
} from "../../api/ProveedoresyFabricas/fabricasApi";
import LogisticaPedidosMatricesReferencia from "./LogisticaPedidosMatricesReferencia";
import {
  normStr,
  packLinepediFromGridPure,
  lineasPackMatch,
  matrizToNumber,
  fmtQtyMatriz,
  tallaLabel,
  colorLabel,
  sortFamiliesByKind,
  sumOrderUnitsReferenceFromPedidoLinks,
  orderUnitsReferenceFromPedido,
} from "../Produccion/a3PedidoMatrizUtils";
import {
  emptyEnvioHeader,
  headerFromEnvio,
  inputDate,
  maxDateInput,
  mergeConsolidacionCabecera,
  parseOptionalDecimal,
  parseOptionalInt,
} from "./logisticaUtils";
import {
  buildEnvioHeaderSummaryGroups,
  buildEnvioHeaderUpdatePayload,
  buildEnvioLineasSavePayload,
  buildPedidoCabecerasSavePayload,
  envioSupportsMatrixEdit,
  factoryNamesFromProveedorRows,
  factorySuggestionsFromConsolidacion,
  collectPedidoProveedorKeys,
  mergeFactorySelectOptions,
  factoryNameFromRemarks,
  lineasToState,
  flattenMultiPedidoLineasPayload,
  resolveShipmentMethodDisplay,
  totalUnitsFromEnvioLineas,
} from "./logisticaEnvioEditUtils";

const SHIPMENT_METHOD_OPTIONS = [
  "Air",
  "Sea",
  "SEA-AIR",
  "Truck",
  "Courier",
  "Combination",
];

function scrollToCabeceraGroup(groupId) {
  document
    .getElementById(`cabecera-grupo-${groupId}`)
    ?.scrollIntoView({ behavior: "smooth", block: "center" });
}

function HeaderFieldGroup({
  id,
  title,
  icon,
  accent,
  filled,
  total,
  children,
  sx,
}) {
  const theme = useTheme();
  const complete = total > 0 && filled === total;
  return (
    <Box
      id={id}
      sx={{
        height: "100%",
        minWidth: 0,
        scrollMarginTop: 16,
        ...sx,
      }}
    >
      <Stack
        direction="row"
        spacing={0.6}
        alignItems="center"
        sx={{
          px: 1,
          py: 0.65,
          borderRadius: 1.2,
          borderLeft: "3px solid",
          borderLeftColor: accent,
          backgroundColor: alpha(
            accent,
            theme.palette.mode === "dark" ? 0.16 : 0.07,
          ),
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: accent,
            backgroundColor: alpha(accent, 0.14),
            flexShrink: 0,
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 16 } })}
        </Box>
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 800,
            letterSpacing: 0.35,
            textTransform: "uppercase",
            color: "text.secondary",
            lineHeight: 1,
          }}
        >
          {title}
        </Typography>
        {total > 0 ? (
          <Typography
            sx={{
              ml: "auto",
              fontSize: "0.72rem",
              fontWeight: 800,
              color: complete ? "success.main" : "text.secondary",
            }}
          >
            {filled}/{total}
          </Typography>
        ) : null}
      </Stack>
      <Box
        sx={{
          pt: 1.2,
          display: "flex",
          flexDirection: "column",
          gap: 1.65,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function CabeceraField({ sx, ...props }) {
  return (
    <TextField
      size="small"
      fullWidth
      hiddenLabel={false}
      InputLabelProps={{ shrink: true }}
      sx={sx}
      {...props}
    />
  );
}

function getShipmentMethodMeta(method, theme) {
  const display = resolveShipmentMethodDisplay(method);
  const iconByKind = {
    seaAir: <AltRouteOutlinedIcon sx={{ fontSize: 20 }} />,
    air: <FlightTakeoffOutlinedIcon sx={{ fontSize: 20 }} />,
    sea: <DirectionsBoatOutlinedIcon sx={{ fontSize: 20 }} />,
    combination: <AltRouteOutlinedIcon sx={{ fontSize: 20 }} />,
    truck: <LocalShippingOutlinedIcon sx={{ fontSize: 20 }} />,
    courier: <Inventory2OutlinedIcon sx={{ fontSize: 20 }} />,
  };
  return {
    label: display.label,
    color: display.color,
    icon: iconByKind[display.kind] || iconByKind.truck,
  };
}

/**
 * Editar cabecera y matriz de un envío ya guardado (PUT + PUT lineas).
 * Si las líneas no comparten la misma pareja de familias, solo se edita la cabecera.
 */
const LogisticaEnvioEditDialog = ({
  open,
  onClose,
  envio,
  onSaved,
  dbSource = "STAGE_DESIGN",
  pedidoRow = null,
  articulo: articuloProp = null,
  presupuesto = null,
  /** Pedido desde el que se abrió el modal (resalta chip). */
  currentPedidoId = null,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [consolidacion, setConsolidacion] = useState(null);
  const [loadingConsolidacion, setLoadingConsolidacion] = useState(false);
  const [header, setHeader] = useState(() => emptyEnvioHeader());
  const [lineasTallas, setLineasTallas] = useState([]);
  const [forwarderOptions, setForwarderOptions] = useState([]);
  const [factoryOptions, setFactoryOptions] = useState([]);
  const [matrixFamiliaH, setMatrixFamiliaH] = useState("");
  const [matrixFamiliaV, setMatrixFamiliaV] = useState("");
  const [tallasH, setTallasH] = useState([]);
  const [tallasV, setTallasV] = useState([]);
  const [familiasTallaCols, setFamiliasTallaCols] = useState([]);
  const [familiasTallaRows, setFamiliasTallaRows] = useState([]);
  const [articuloMaestro, setArticuloMaestro] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loadingMatriz, setLoadingMatriz] = useState(false);
  const [pedidoHeaders, setPedidoHeaders] = useState({});
  const [pedidoLineasByPedido, setPedidoLineasByPedido] = useState({});
  const [unlinkingPedidoId, setUnlinkingPedidoId] = useState(null);
  const [removePedidoDialog, setRemovePedidoDialog] = useState(null);

  const idEnvio = envio?.IdLogisticaEnvio;
  const source = detail ?? envio;
  const softFieldSx = {
    "& .MuiInputLabel-root": {
      fontWeight: 600,
      fontSize: "0.8rem",
      color: theme.palette.text.secondary,
    },
    "& .MuiInputBase-root": {
      fontSize: "0.875rem",
      backgroundColor: alpha(
        theme.palette.background.paper,
        theme.palette.mode === "dark" ? 0.52 : 0.98,
      ),
      borderRadius: 1.2,
      "& fieldset": {
        borderColor: alpha(theme.palette.divider, 0.9),
      },
      "&:hover fieldset": {
        borderColor: alpha(theme.palette.primary.main, 0.28),
      },
    },
    "& .MuiInputBase-input": {
      py: 0.8,
      fontSize: "0.875rem",
    },
    "& .MuiNativeSelect-select": {
      py: 0.8,
      fontSize: "0.875rem",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  };

  const pedidoLinksArr = useMemo(
    () => (Array.isArray(source?.pedidoLinks) ? source.pedidoLinks : []),
    [source],
  );
  const nPedidos = pedidoLinksArr.length;
  const multiPedido = nPedidos >= 2;
  const seedPedidoId =
    Number.isFinite(Number(currentPedidoId)) && Number(currentPedidoId) > 0
      ? Number(currentPedidoId)
      : Number(
          pedidoLinksArr[0]?.pedidoA3?.IdPresupuestoA3Pedido ??
            pedidoLinksArr[0]?.IdPresupuestoA3Pedido,
        ) || null;

  /** Pedido A3 desde el que copiar matriz: prop explícita o único vínculo del envío (p. ej. gestión logística). */
  const effectivePedidoRow = useMemo(() => {
    const hasProp =
      pedidoRow &&
      (String(pedidoRow.CodArt ?? "").trim() ||
        String(pedidoRow.Referencia ?? "").trim() ||
        (pedidoRow.IdPedv != null && String(pedidoRow.IdPedv).trim() !== "") ||
        (pedidoRow.IdPedc != null && String(pedidoRow.IdPedc).trim() !== ""));
    if (hasProp) return pedidoRow;
    if (pedidoLinksArr.length === 1) return pedidoLinksArr[0]?.pedidoA3 ?? null;
    return null;
  }, [pedidoRow, pedidoLinksArr]);

  const a3DbSource = useMemo(
    () =>
      String(
        effectivePedidoRow?.EmpresaA3 ||
          pedidoRow?.EmpresaA3 ||
          pedidoLinksArr[0]?.pedidoA3?.EmpresaA3 ||
          dbSource ||
          "STAGE_DESIGN",
      ).trim() || "STAGE_DESIGN",
    [
      effectivePedidoRow?.EmpresaA3,
      pedidoRow?.EmpresaA3,
      pedidoLinksArr,
      dbSource,
    ],
  );

  const matrixViewOk = useMemo(
    () => envioSupportsMatrixEdit(source?.lineas),
    [source],
  );
  const editableMatrix = matrixViewOk && !multiPedido;

  const refreshEnvioData = useCallback(async () => {
    if (!idEnvio) return;
    setLoadingDetail(true);
    setLoadingConsolidacion(true);
    try {
      const [detailData, consolidacionData] = await Promise.all([
        getLogisticaEnvioDetalle(idEnvio),
        getLogisticaEnvioConsolidacionRef(idEnvio).catch(() => null),
      ]);
      setDetail(detailData);
      setConsolidacion(consolidacionData);
    } finally {
      setLoadingDetail(false);
      setLoadingConsolidacion(false);
    }
  }, [idEnvio]);

  const codArtResolved = useMemo(() => {
    if (
      effectivePedidoRow?.CodArt != null &&
      String(effectivePedidoRow.CodArt).trim()
    )
      return String(effectivePedidoRow.CodArt).trim();
    const links = Array.isArray(source?.pedidoLinks) ? source.pedidoLinks : [];
    for (const l of links) {
      const c = String(l?.pedidoA3?.CodArt ?? "").trim();
      if (c) return c;
    }
    return "";
  }, [effectivePedidoRow, source]);

  useEffect(() => {
    if (!open || !idEnvio) {
      setDetail(null);
      return;
    }
    setLoadingDetail(true);
    setDetail(null);
    let cancelled = false;
    getLogisticaEnvioDetalle(idEnvio)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch(() => {
        if (!cancelled) setDetail(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, idEnvio]);

  useEffect(() => {
    if (!open || !multiPedido) {
      setPedidoHeaders({});
      setPedidoLineasByPedido({});
      return;
    }
    const next = {};
    const srcCabeceras = Array.isArray(source?.pedidoCabeceras)
      ? source.pedidoCabeceras
      : [];
    const baseHeader = headerFromEnvio(source);
    for (const row of srcCabeceras) {
      const idPed = Number(row?.IdPresupuestoA3Pedido);
      if (!Number.isFinite(idPed) || idPed <= 0) continue;
      const entry = {
        Id: row?.Id ?? null,
        IdPresupuestoA3Pedido: idPed,
        NCartons: row?.NCartons != null ? String(row.NCartons) : "",
        Unidades: row?.Unidades != null ? String(row.Unidades) : "",
        GrossWeightKgs:
          row?.GrossWeightKgs != null ? String(row.GrossWeightKgs) : "",
        TotalVolumeCbm:
          row?.TotalVolumeCbm != null ? String(row.TotalVolumeCbm) : "",
        ETA: inputDate(row?.ETA),
        BookingDone: inputDate(row?.BookingDone),
        InspectionDate: inputDate(row?.InspectionDate),
        ExFD: inputDate(row?.ExFD),
        ETDF: inputDate(row?.ETDF),
        CutOff: inputDate(row?.CutOff),
        ETD: inputDate(row?.ETD),
        DD: inputDate(row?.DD),
        DocumentsApproved: inputDate(row?.DocumentsApproved),
        OriginalTelexDocsSemt: inputDate(row?.OriginalTelexDocsSemt),
        ShipmentMethod: String(row?.ShipmentMethod ?? ""),
        Forwarder: String(row?.Forwarder ?? ""),
        ShipmentCodeCustomer: String(row?.ShipmentCodeCustomer ?? ""),
        BLAWBNr: String(row?.BLAWBNr ?? ""),
        Remarks: String(row?.Remarks ?? ""),
        POReference: String(row?.POReference ?? ""),
      };
      const isSeed = Number.isFinite(seedPedidoId) && seedPedidoId === idPed;
      next[idPed] = isSeed
        ? {
            ...entry,
            NCartons: entry.NCartons || baseHeader.NCartons || "",
            GrossWeightKgs:
              entry.GrossWeightKgs || baseHeader.GrossWeightKgs || "",
            TotalVolumeCbm:
              entry.TotalVolumeCbm || baseHeader.TotalVolumeCbm || "",
            ETA: entry.ETA || baseHeader.ETA || "",
            BookingDone: entry.BookingDone || baseHeader.BookingDone || "",
            InspectionDate:
              entry.InspectionDate || baseHeader.InspectionDate || "",
            ExFD: entry.ExFD || baseHeader.ExFD || "",
            ETDF: entry.ETDF || baseHeader.ETDF || "",
            CutOff: entry.CutOff || baseHeader.CutOff || "",
            ETD: entry.ETD || baseHeader.ETD || "",
            DD: entry.DD || baseHeader.DD || "",
            DocumentsApproved:
              entry.DocumentsApproved || baseHeader.DocumentsApproved || "",
            OriginalTelexDocsSemt:
              entry.OriginalTelexDocsSemt ||
              baseHeader.OriginalTelexDocsSemt ||
              "",
            ShipmentMethod:
              entry.ShipmentMethod || baseHeader.ShipmentMethod || "",
            Forwarder: entry.Forwarder || baseHeader.Forwarder || "",
            ShipmentCodeCustomer:
              entry.ShipmentCodeCustomer ||
              baseHeader.ShipmentCodeCustomer ||
              "",
            BLAWBNr: entry.BLAWBNr || baseHeader.BLAWBNr || "",
            Remarks: entry.Remarks || baseHeader.Remarks || "",
            POReference: entry.POReference || baseHeader.POReference || "",
          }
        : entry;
    }
    setPedidoHeaders(next);
  }, [open, multiPedido, source, seedPedidoId]);

  useEffect(() => {
    if (!multiPedido) return;
    const rows = Object.values(pedidoHeaders || {});
    if (!rows.length) return;
    const rowsWithData = rows.filter((r) => {
      const hasNums =
        parseOptionalInt(r.NCartons) != null ||
        parseOptionalDecimal(r.GrossWeightKgs) != null ||
        parseOptionalDecimal(r.TotalVolumeCbm) != null;
      const hasDates = Boolean(
        String(r.ETA || "").trim() ||
        String(r.BookingDone || "").trim() ||
        String(r.InspectionDate || "").trim() ||
        String(r.ExFD || "").trim() ||
        String(r.ETDF || "").trim() ||
        String(r.CutOff || "").trim() ||
        String(r.ETD || "").trim() ||
        String(r.DD || "").trim() ||
        String(r.DocumentsApproved || "").trim() ||
        String(r.OriginalTelexDocsSemt || "").trim(),
      );
      const hasShipment = Boolean(String(r.ShipmentMethod || "").trim());
      return hasNums || hasDates || hasShipment;
    });
    // Si no hay mini-cabeceras reales, no pises la cabecera SQL del envío.
    if (!rowsWithData.length) return;
    const sumCartons = rowsWithData.reduce(
      (s, r) => s + (parseOptionalInt(r.NCartons) || 0),
      0,
    );
    const sumKg = rowsWithData.reduce(
      (s, r) => s + (parseOptionalDecimal(r.GrossWeightKgs) || 0),
      0,
    );
    const sumCbm = rowsWithData.reduce(
      (s, r) => s + (parseOptionalDecimal(r.TotalVolumeCbm) || 0),
      0,
    );
    const eta = maxDateInput(rowsWithData.map((r) => r.ETA));
    const booking = maxDateInput(rowsWithData.map((r) => r.BookingDone));
    const inspectionDate = maxDateInput(
      rowsWithData.map((r) => r.InspectionDate),
    );
    const exfd = maxDateInput(rowsWithData.map((r) => r.ExFD));
    const etdf = maxDateInput(rowsWithData.map((r) => r.ETDF));
    const cutOff = maxDateInput(rowsWithData.map((r) => r.CutOff));
    const etd = maxDateInput(rowsWithData.map((r) => r.ETD));
    const dd = maxDateInput(rowsWithData.map((r) => r.DD));
    const docs = maxDateInput(rowsWithData.map((r) => r.DocumentsApproved));
    const telex = maxDateInput(
      rowsWithData.map((r) => r.OriginalTelexDocsSemt),
    );
    const sm =
      rowsWithData.find((r) => String(r.ShipmentMethod || "").trim())
        ?.ShipmentMethod || "";
    setHeader((h) => ({
      ...h,
      NCartons: String(sumCartons),
      GrossWeightKgs: String(sumKg),
      TotalVolumeCbm: String(sumCbm),
      ETA: eta || h.ETA,
      BookingDone: booking || h.BookingDone,
      InspectionDate: inspectionDate || h.InspectionDate,
      ExFD: exfd || h.ExFD,
      ETDF: etdf || h.ETDF,
      CutOff: cutOff || h.CutOff,
      ETD: etd || h.ETD,
      DD: dd || h.DD,
      DocumentsApproved: docs || h.DocumentsApproved,
      OriginalTelexDocsSemt: telex || h.OriginalTelexDocsSemt,
      ShipmentMethod: sm || h.ShipmentMethod,
    }));
  }, [multiPedido, pedidoHeaders]);

  useEffect(() => {
    if (!open || !idEnvio) {
      setConsolidacion(null);
      return;
    }
    setLoadingConsolidacion(true);
    let cancelled = false;
    getLogisticaEnvioConsolidacionRef(idEnvio)
      .then((d) => {
        if (!cancelled) setConsolidacion(d);
      })
      .catch(() => {
        if (!cancelled) setConsolidacion(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingConsolidacion(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, idEnvio]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getForwarderProveedores()
      .then((rows) => {
        if (!cancelled) setForwarderOptions(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setForwarderOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const proveedorKeys = useMemo(
    () => collectPedidoProveedorKeys(pedidoLinksArr, effectivePedidoRow),
    [pedidoLinksArr, effectivePedidoRow],
  );

  useEffect(() => {
    if (!open) {
      setFactoryOptions([]);
      return;
    }
    let cancelled = false;
    const loadFactories = async () => {
      const names = [];
      try {
        for (const codPro of proveedorKeys.codPros) {
          try {
            const rows = await getFabricasByProveedor(codPro);
            names.push(...factoryNamesFromProveedorRows(rows));
          } catch {
            /* proveedor sin fábricas o 404 */
          }
        }
        if (!names.length) {
          for (const nombre of proveedorKeys.nombres) {
            try {
              const rows = await getFabricasNombresByProveedorNombre(nombre);
              names.push(...factoryNamesFromProveedorRows(rows));
            } catch {
              /* mismo origen que la logística legacy */
            }
          }
        }
      } finally {
        if (!cancelled) setFactoryOptions(mergeFactorySelectOptions(names));
      }
    };
    void loadFactories();
    return () => {
      cancelled = true;
    };
  }, [open, proveedorKeys]);

  useEffect(() => {
    if (!open || loadingDetail) return;
    const e = detail ?? envio;
    if (!e) return;
    setError("");
    const linksLen = Array.isArray(e.pedidoLinks) ? e.pedidoLinks.length : 0;
    const base = headerFromEnvio(e);
    const merged =
      !loadingConsolidacion &&
      consolidacion?.productosConReferencia &&
      linksLen >= 2
        ? mergeConsolidacionCabecera(base, consolidacion)
        : base;
    setHeader(merged);
    const raw = Array.isArray(e.lineas) ? e.lineas : [];
    setLineasTallas(lineasToState(raw));
    if (raw.length) {
      setMatrixFamiliaH(
        String(raw[0].CodFamTallaH ?? raw[0].codFamTallaH ?? "").trim(),
      );
      setMatrixFamiliaV(
        String(raw[0].CodFamTallaV ?? raw[0].codFamTallaV ?? "").trim(),
      );
    } else {
      setMatrixFamiliaH("");
      setMatrixFamiliaV("");
    }
    setArticuloMaestro(articuloProp || null);
  }, [
    open,
    idEnvio,
    loadingDetail,
    detail,
    envio,
    articuloProp,
    consolidacion,
    loadingConsolidacion,
  ]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [familiasData, familiasClasData] = await Promise.all([
          getA3FamiliasTalla("", a3DbSource),
          getA3FamiliasTallaClasificadas("", a3DbSource),
        ]);
        if (cancelled) return;
        const famAll = Array.isArray(familiasData?.rows)
          ? familiasData.rows
          : [];
        const cols = Array.isArray(familiasClasData?.rowsTalla)
          ? familiasClasData.rowsTalla
          : [];
        const rowsFam = Array.isArray(familiasClasData?.rowsColor)
          ? familiasClasData.rowsColor
          : [];
        setFamiliasTallaCols(cols.length ? cols : famAll);
        setFamiliasTallaRows(rowsFam.length ? rowsFam : famAll);
      } catch (_e) {
        if (!cancelled) {
          setFamiliasTallaCols([]);
          setFamiliasTallaRows([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, a3DbSource]);

  useEffect(() => {
    if (!open || !matrixFamiliaH) {
      setTallasH([]);
      return;
    }
    let active = true;
    getA3Tallas(matrixFamiliaH, "", a3DbSource)
      .then((data) => {
        if (!active) return;
        setTallasH(Array.isArray(data?.rows) ? data.rows : []);
      })
      .catch(() => {
        if (active) setTallasH([]);
      });
    return () => {
      active = false;
    };
  }, [open, matrixFamiliaH, a3DbSource]);

  useEffect(() => {
    if (!open || !matrixFamiliaV) {
      setTallasV([]);
      return;
    }
    let active = true;
    getA3Tallas(matrixFamiliaV, "", a3DbSource)
      .then((data) => {
        if (!active) return;
        setTallasV(Array.isArray(data?.rows) ? data.rows : []);
      })
      .catch(() => {
        if (active) setTallasV([]);
      });
    return () => {
      active = false;
    };
  }, [open, matrixFamiliaV, a3DbSource]);

  useEffect(() => {
    if (!open || !codArtResolved) return;
    let cancelled = false;
    searchA3Articulos(codArtResolved, a3DbSource)
      .then((data) => {
        if (cancelled) return;
        const ar = Array.isArray(data?.rows) ? data.rows : [];
        const hit =
          ar.find(
            (x) => normStr(x.CODART ?? x.codart) === normStr(codArtResolved),
          ) ??
          ar[0] ??
          null;
        if (hit) setArticuloMaestro(hit);
        else if (articuloProp) setArticuloMaestro(articuloProp);
      })
      .catch(() => {
        if (!cancelled && articuloProp) setArticuloMaestro(articuloProp);
      });
    return () => {
      cancelled = true;
    };
  }, [open, codArtResolved, a3DbSource, articuloProp]);

  const artForPack = articuloMaestro || articuloProp;
  const artFamilyH = normStr(
    artForPack?.CODFAMTALLAH ?? artForPack?.codfamtallah ?? "",
  );
  const artFamilyV = normStr(
    artForPack?.CODFAMTALLAV ?? artForPack?.codfamtallav ?? "",
  );
  const lockFamiliesToArticulo = Boolean(artFamilyH && artFamilyV);
  const resolvedFamilyH = artFamilyH || normStr(matrixFamiliaH);
  const resolvedFamilyV = artFamilyV || normStr(matrixFamiliaV);

  const familiesColumnOptions = useMemo(() => {
    if (lockFamiliesToArticulo) {
      return familiasTallaCols.filter(
        (f) => normStr(f.CODFAMTALLA) === resolvedFamilyH,
      );
    }
    return sortFamiliesByKind(
      familiasTallaCols.filter(
        (f) =>
          !normStr(matrixFamiliaV) ||
          normStr(f.CODFAMTALLA) !== normStr(matrixFamiliaV),
      ),
      "talla",
    );
  }, [
    familiasTallaCols,
    lockFamiliesToArticulo,
    resolvedFamilyH,
    matrixFamiliaV,
  ]);

  const familiesRowOptions = useMemo(() => {
    if (lockFamiliesToArticulo) {
      return familiasTallaRows.filter(
        (f) => normStr(f.CODFAMTALLA) === resolvedFamilyV,
      );
    }
    return sortFamiliesByKind(
      familiasTallaRows.filter(
        (f) =>
          !normStr(matrixFamiliaH) ||
          normStr(f.CODFAMTALLA) !== normStr(matrixFamiliaH),
      ),
      "color",
    );
  }, [
    familiasTallaRows,
    lockFamiliesToArticulo,
    resolvedFamilyV,
    matrixFamiliaH,
  ]);

  const totalMatriz = useMemo(
    () =>
      lineasTallas.reduce((acc, row) => acc + matrizToNumber(row.unidades), 0),
    [lineasTallas],
  );

  const unitsCabeceraEstimate = useMemo(() => {
    const fromProp = matrizToNumber(presupuesto?.Units);
    if (fromProp > 0) return fromProp;
    const fromLinks = sumOrderUnitsReferenceFromPedidoLinks(pedidoLinksArr);
    return fromLinks > 0 ? fromLinks : 0;
  }, [presupuesto?.Units, pedidoLinksArr]);
  const mismatchMatrizVsEstimate =
    unitsCabeceraEstimate > 0 &&
    lineasTallas.length > 0 &&
    Math.abs(totalMatriz - unitsCabeceraEstimate) > 0.0001;

  const qtyForCell = (rowTalla, colTalla) => {
    const p = packLinepediFromGridPure(
      artForPack,
      matrixFamiliaH,
      matrixFamiliaV,
      colTalla,
      rowTalla,
    );
    if (!p) return 0;
    const r = lineasTallas.find((l) => lineasPackMatch(l, p));
    return r ? matrizToNumber(r.unidades) : 0;
  };

  const hasMatrixCell = (rowTalla, colTalla) => {
    const p = packLinepediFromGridPure(
      artForPack,
      matrixFamiliaH,
      matrixFamiliaV,
      colTalla,
      rowTalla,
    );
    if (!p) return false;
    return lineasTallas.some((l) => lineasPackMatch(l, p));
  };

  const updateMatrixCell = (rowTalla, colTalla, rawValue) => {
    const p = packLinepediFromGridPure(
      artForPack,
      matrixFamiliaH,
      matrixFamiliaV,
      colTalla,
      rowTalla,
    );
    if (!p) return;
    const unidades = matrizToNumber(rawValue);
    if (unidades < 0) return;
    setLineasTallas((prev) => {
      const idx = prev.findIndex((r) => lineasPackMatch(r, p));
      const nextRow = {
        codFamTallaH: p.codFamTallaH,
        codFamTallaV: p.codFamTallaV,
        codTallaH: normStr(p.codTallaH),
        codTallaV: normStr(p.codTallaV),
        unidades,
      };
      if (idx < 0) return [...prev, nextRow];
      const next = [...prev];
      next[idx] = nextRow;
      return next;
    });
  };

  const colTotals = useMemo(() => {
    if (!normStr(matrixFamiliaH) || !normStr(matrixFamiliaV))
      return tallasH.map(() => 0);
    return tallasH.map((h) =>
      tallasV.reduce((sum, v) => {
        const p = packLinepediFromGridPure(
          artForPack,
          matrixFamiliaH,
          matrixFamiliaV,
          h,
          v,
        );
        const r = p ? lineasTallas.find((l) => lineasPackMatch(l, p)) : null;
        return sum + (r ? matrizToNumber(r.unidades) : 0);
      }, 0),
    );
  }, [
    artForPack,
    tallasH,
    tallasV,
    lineasTallas,
    matrixFamiliaH,
    matrixFamiliaV,
  ]);

  const loadMatrizFromPedidoA3 = useCallback(async () => {
    const row = effectivePedidoRow;
    const codart =
      String(row?.CodArt || "").trim() || String(row?.Referencia || "").trim();
    const idPc =
      row?.IdPedc != null && row?.IdPedc !== "" ? Number(row.IdPedc) : NaN;
    const idPv =
      row?.IdPedv != null && row?.IdPedv !== "" ? Number(row.IdPedv) : NaN;
    const idPedc = Number.isFinite(idPc) && idPc > 0 ? idPc : null;
    const idPedv = Number.isFinite(idPv) && idPv > 0 ? idPv : null;
    if (!codart || (!idPedc && !idPedv)) {
      setError("No hay datos suficientes para copiar la matriz desde A3.");
      return;
    }
    setLoadingMatriz(true);
    setError("");
    try {
      const data = await getA3PedidoLineasMatriz({
        idPedc: idPedc ?? "",
        idPedv: idPedv ?? "",
        codart,
        dbSource: a3DbSource,
      });
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      if (!rows.length) {
        setError("A3 no devolvió líneas de matriz para este pedido.");
        return;
      }
      const ar = await searchA3Articulos(codart, a3DbSource).catch(() => ({
        rows: [],
      }));
      const maestroHit =
        (Array.isArray(ar?.rows) ? ar.rows : []).find(
          (x) => normStr(x.CODART ?? x.codart) === normStr(codart),
        ) || null;
      if (maestroHit) setArticuloMaestro(maestroHit);
      setLineasTallas(
        rows.map((x) => ({
          codFamTallaH: String(x.codFamTallaH || "").trim(),
          codTallaH: String(x.codTallaH || "").trim(),
          codFamTallaV: String(x.codFamTallaV || "").trim(),
          codTallaV: String(x.codTallaV || "").trim(),
          unidades: matrizToNumber(x.unidades),
        })),
      );
      const ah = normStr(maestroHit?.CODFAMTALLAH ?? maestroHit?.codfamtallah);
      const av = normStr(maestroHit?.CODFAMTALLAV ?? maestroHit?.codfamtallav);
      if (ah && av && ah !== av) {
        setMatrixFamiliaH(
          String(
            maestroHit.CODFAMTALLAH ?? maestroHit.codfamtallah ?? "",
          ).trim(),
        );
        setMatrixFamiliaV(
          String(
            maestroHit.CODFAMTALLAV ?? maestroHit.codfamtallav ?? "",
          ).trim(),
        );
      } else {
        setMatrixFamiliaH(String(rows[0].codFamTallaH || "").trim());
        setMatrixFamiliaV(String(rows[0].codFamTallaV || "").trim());
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoadingMatriz(false);
    }
  }, [effectivePedidoRow, a3DbSource]);

  const handleSave = async () => {
    if (!idEnvio) return;
    setSaving(true);
    setError("");
    try {
      await updateLogisticaEnvio(
        idEnvio,
        buildEnvioHeaderUpdatePayload(header),
      );
      if (editableMatrix) {
        await updateLogisticaEnvioLineas(
          idEnvio,
          buildEnvioLineasSavePayload(lineasTallas),
        );
      }
      if (multiPedido && Object.keys(pedidoLineasByPedido || {}).length > 0) {
        await updateLogisticaEnvioLineas(
          idEnvio,
          flattenMultiPedidoLineasPayload(pedidoLineasByPedido),
        );
      }
      if (multiPedido) {
        await updateLogisticaEnvioPedidoCabeceras(
          idEnvio,
          buildPedidoCabecerasSavePayload(pedidoHeaders),
        );
      }
      if (onSaved) await onSaved();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const aplicarCabeceraDesdeTimeline = useCallback(() => {
    if (!consolidacion?.productosConReferencia) return;
    setHeader((h) => mergeConsolidacionCabecera(h, consolidacion));
  }, [consolidacion]);

  const handleRemovePedido = useCallback(async () => {
    const idPedido = Number(removePedidoDialog?.idPedido);
    if (
      !idEnvio ||
      !Number.isFinite(idPedido) ||
      idPedido <= 0 ||
      !multiPedido
    ) {
      return;
    }
    setUnlinkingPedidoId(idPedido);
    setError("");
    try {
      await deleteVinculoPedidoLogisticaEnvio(idEnvio, idPedido);
      setRemovePedidoDialog(null);
      await refreshEnvioData();
      if (typeof onSaved === "function") {
        await onSaved();
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setUnlinkingPedidoId(null);
    }
  }, [idEnvio, multiPedido, onSaved, refreshEnvioData, removePedidoDialog]);

  const requestRemovePedido = useCallback(
    (idPed) => {
      const idPedido = Number(idPed);
      if (!Number.isFinite(idPedido) || idPedido <= 0) return;
      const hit = pedidoLinksArr.find(
        (lnk) =>
          Number(
            lnk?.pedidoA3?.IdPresupuestoA3Pedido ?? lnk?.IdPresupuestoA3Pedido,
          ) === idPedido,
      );
      const referencia = String(hit?.pedidoA3?.Referencia ?? "").trim();
      setRemovePedidoDialog({
        idPedido,
        label: referencia ? `#${idPedido} · ${referencia}` : `#${idPedido}`,
      });
    },
    [pedidoLinksArr],
  );

  const forwarderSelectOptions = useMemo(() => {
    const current = String(header.Forwarder || "").trim();
    if (current && !forwarderOptions.includes(current)) {
      return [current, ...forwarderOptions];
    }
    return forwarderOptions;
  }, [forwarderOptions, header.Forwarder]);

  const factorySuggestions = useMemo(
    () => factorySuggestionsFromConsolidacion(consolidacion),
    [consolidacion],
  );

  const remarksFactory = useMemo(
    () => factoryNameFromRemarks(header.Remarks),
    [header.Remarks],
  );

  const factorySelectOptions = useMemo(
    () =>
      mergeFactorySelectOptions(factoryOptions, factorySuggestions, [
        header.Factory,
        remarksFactory,
      ]),
    [factoryOptions, factorySuggestions, header.Factory, remarksFactory],
  );

  const showFactoryFromRemarks =
    Boolean(remarksFactory) &&
    normStr(header.Factory) !== normStr(remarksFactory);

  if (!open || !envio) return null;

  const sumUnitsRef = sumOrderUnitsReferenceFromPedidoLinks(pedidoLinksArr);
  const matUnitsSaved = totalUnitsFromEnvioLineas(source?.lineas);
  const curPid = Number(currentPedidoId);
  const multiPedidoMatrixEditable = multiPedido;
  const shipmentMethodMeta = getShipmentMethodMeta(
    header.ShipmentMethod,
    theme,
  );
  const headerGroupAccents = {
    production: theme.palette.info.main,
    cargo: theme.palette.warning.main,
    shipment: theme.palette.primary.main,
    tracking: theme.palette.success.main,
    documents: theme.palette.secondary.main,
  };
  const summaryGroups = buildEnvioHeaderSummaryGroups(header, {
    methodLabel: shipmentMethodMeta.label,
  });
  const groupFill = Object.fromEntries(
    summaryGroups.map((g) => [g.id, { filled: g.filled, total: g.total }]),
  );
  const summaryCards = [
    {
      label: "PO",
      groupId: "production",
      value: header.POReference || "—",
      color: theme.palette.primary.main,
      icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "Metodo",
      groupId: "shipment",
      value: shipmentMethodMeta.label,
      color: shipmentMethodMeta.color,
      icon: shipmentMethodMeta.icon,
    },
    {
      label: "Forwarder",
      groupId: "shipment",
      value: header.Forwarder || "—",
      color: theme.palette.text.primary,
      icon: <AssignmentOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "Cartons",
      groupId: "cargo",
      value: header.NCartons || "—",
      color: theme.palette.info.main,
      icon: <Inventory2OutlinedIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "Kg",
      groupId: "cargo",
      value: header.GrossWeightKgs || "—",
      color: theme.palette.error.main,
      icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "CBM",
      groupId: "cargo",
      value: header.TotalVolumeCbm || "—",
      color: theme.palette.success.main,
      icon: <Inventory2OutlinedIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "Inspection",
      groupId: "production",
      value: inputDate(header.InspectionDate) || "—",
      color: theme.palette.error.main,
      icon: <EventOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "ExFD",
      groupId: "shipment",
      value: inputDate(header.ExFD) || "—",
      color: theme.palette.secondary.main,
      icon: <EventOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "ETDF",
      groupId: "shipment",
      value: inputDate(header.ETDF) || "—",
      color: theme.palette.secondary.dark,
      icon: <EventOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "CUT-OFF",
      groupId: "shipment",
      value: inputDate(header.CutOff) || "—",
      color: theme.palette.warning.dark,
      icon: <EventOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "ETD",
      groupId: "shipment",
      value: inputDate(header.ETD) || "—",
      color: theme.palette.primary.dark,
      icon: <EventOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "Booking",
      groupId: "shipment",
      value: inputDate(header.BookingDone) || "—",
      color: theme.palette.warning.main,
      icon: <EventOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "ETA",
      groupId: "tracking",
      value: inputDate(header.ETA) || "—",
      color: theme.palette.info.main,
      icon: <EventOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "DD",
      groupId: "tracking",
      value: inputDate(header.DD) || "—",
      color: theme.palette.success.dark,
      icon: <EventOutlinedIcon sx={{ fontSize: 16 }} />,
    },
  ];
  const factoryLabel = String(header.Factory || "").trim();

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 12px 36px rgba(0,0,0,0.52)"
              : "0 12px 36px rgba(0,0,0,0.16)",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2.1,
          borderBottom: "1px solid",
          borderColor: alpha(theme.palette.primary.main, 0.12),
          background: `linear-gradient(135deg, ${alpha(shipmentMethodMeta.color, 0.16)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 52%, ${alpha(theme.palette.background.paper, 0.94)} 100%)`,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 1.25, md: 2 }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.4} alignItems="flex-start">
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: 2.4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: shipmentMethodMeta.color,
                backgroundColor: alpha(shipmentMethodMeta.color, 0.14),
                border: "1px solid",
                borderColor: alpha(shipmentMethodMeta.color, 0.2),
                boxShadow: `0 8px 20px ${alpha(shipmentMethodMeta.color, 0.16)}`,
                flexShrink: 0,
              }}
            >
              {shipmentMethodMeta.icon}
            </Box>
            <Box>
              <Stack
                direction="row"
                gap={0.75}
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 0.55 }}
              >
                <Chip
                  size="small"
                  label={`Envio #${idEnvio}`}
                  color="primary"
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${nPedidos || 0} pedido${nPedidos === 1 ? "" : "s"}`}
                />
                {factoryLabel ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<FactoryOutlinedIcon sx={{ fontSize: 16 }} />}
                    label={factoryLabel}
                    sx={{
                      maxWidth: 260,
                      "& .MuiChip-label": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                ) : null}
                {Number.isFinite(curPid) && curPid > 0 ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    color="secondary"
                    label={`Abierto desde pedido #${curPid}`}
                  />
                ) : null}
              </Stack>
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, lineHeight: 1.1 }}
              >
                {t("logisticaPedidos.editEnvioDialogTitle", { id: idEnvio })}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.45 }}
              >
                Ajusta la cabecera logistica, el transporte y el reparto de
                matrices del envio.
              </Typography>
            </Box>
          </Stack>
          <Box
            sx={{
              minWidth: { xs: "100%", md: 180 },
              px: 1.3,
              py: 1,
              borderRadius: 2,
              border: "1px solid",
              borderColor: alpha(shipmentMethodMeta.color, 0.16),
              backgroundColor: alpha(theme.palette.background.paper, 0.76),
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                color: "text.secondary",
              }}
            >
              Transporte
            </Typography>
            <Stack
              direction="row"
              spacing={0.8}
              alignItems="center"
              sx={{ mt: 0.45 }}
            >
              <Box sx={{ color: shipmentMethodMeta.color, display: "flex" }}>
                {shipmentMethodMeta.icon}
              </Box>
              <Typography
                sx={{ fontWeight: 800, color: shipmentMethodMeta.color }}
              >
                {shipmentMethodMeta.label}
              </Typography>
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.4 }}
            >
              Uds matriz guardada: {fmtQtyMatriz(matUnitsSaved)}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ backgroundColor: alpha(theme.palette.background.default, 0.45) }}
      >
        {loadingDetail ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <Stack spacing={2}>
            {nPedidos > 0 ? (
              <>
                <Box
                  sx={{
                    p: 1.45,
                    border: "1px solid",
                    borderColor: alpha(theme.palette.primary.main, 0.12),
                    borderRadius: 2.4,
                    background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.035)} 0%, ${alpha(theme.palette.background.paper, 0.94)} 100%)`,
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 10px 28px rgba(0,0,0,0.18)"
                        : "0 10px 28px rgba(15,23,42,0.04)",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.25}
                    justifyContent="space-between"
                    sx={{ mb: 1.15 }}
                  >
                    <Box>
                      <Stack
                        direction="row"
                        spacing={0.8}
                        alignItems="center"
                        sx={{ mb: 0.35 }}
                      >
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 1.8,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: theme.palette.primary.main,
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.12,
                            ),
                          }}
                        >
                          <AssignmentOutlinedIcon sx={{ fontSize: 18 }} />
                        </Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 800 }}
                        >
                          {t("logisticaPedidos.envioPedidosEnCarga")}
                        </Typography>
                      </Stack>
                      {/* <Typography variant="caption" color="text.secondary">
                        Vista rapida del envio compartido, su transporte y las
                        magnitudes clave.
                      </Typography> */}
                    </Box>
                    <Stack
                      direction="row"
                      gap={0.75}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {pedidoLinksArr.map((lnk) => {
                        const p = lnk?.pedidoA3;
                        const idp = Number(
                          p?.IdPresupuestoA3Pedido ??
                            lnk?.IdPresupuestoA3Pedido,
                        );
                        const isHere =
                          Number.isFinite(curPid) &&
                          curPid > 0 &&
                          idp === curPid;
                        const label = p?.Referencia
                          ? `#${idp} · ${p.Referencia}`
                          : `#${idp}`;
                        const pidProd = p?.presupuesto?.IdProducto;
                        const uRef = orderUnitsReferenceFromPedido(p);
                        const uLabel =
                          uRef > 0 ? ` · ${fmtQtyMatriz(uRef)} uds (ref.)` : "";
                        return (
                          <Chip
                            key={lnk.Id ?? `${idEnvio}-${idp}`}
                            size="small"
                            variant={isHere ? "filled" : "outlined"}
                            color={isHere ? "primary" : "default"}
                            label={
                              (pidProd != null && String(pidProd).trim() !== ""
                                ? `${label} · IdProducto ${pidProd}`
                                : label) + uLabel
                            }
                            sx={{
                              borderRadius: 999,
                              backgroundColor: isHere
                                ? undefined
                                : alpha(theme.palette.background.paper, 0.9),
                              "& .MuiChip-label": {
                                fontWeight: 700,
                              },
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </Stack>
                  <Alert
                    severity="info"
                    sx={{
                      mb: 1.1,
                      borderRadius: 2,
                      "& .MuiAlert-message": { fontSize: "0.82rem" },
                    }}
                  >
                    {t("logisticaPedidos.envioEditSharedHint", {
                      count: nPedidos,
                      sumUnits: fmtQtyMatriz(sumUnitsRef),
                      matUnits: fmtQtyMatriz(matUnitsSaved),
                    })}
                  </Alert>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 800,
                      display: "block",
                      mb: 0.8,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 0.45,
                    }}
                  >
                    Resumen del envio
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        md: "repeat(7, minmax(0, 1fr))",
                      },
                      gap: 0.9,
                    }}
                  >
                    {summaryCards.map((item) => (
                      <Box
                        key={item.label}
                        role="button"
                        tabIndex={0}
                        onClick={() => scrollToCabeceraGroup(item.groupId)}
                        onKeyDown={(ev) => {
                          if (ev.key === "Enter" || ev.key === " ") {
                            ev.preventDefault();
                            scrollToCabeceraGroup(item.groupId);
                          }
                        }}
                        sx={{
                          borderRadius: 1.8,
                          border: "1px solid",
                          borderColor: alpha(item.color, 0.14),
                          backgroundColor: alpha(
                            item.color,
                            theme.palette.mode === "dark" ? 0.14 : 0.05,
                          ),
                          px: 1.05,
                          py: 0.95,
                          minHeight: 68,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          transition:
                            "border-color 0.15s ease, background-color 0.15s ease",
                          "&:hover": {
                            borderColor: alpha(item.color, 0.4),
                            backgroundColor: alpha(item.color, 0.1),
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={0.7}
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              color: item.color,
                              display: "flex",
                              flexShrink: 0,
                            }}
                          >
                            {item.icon}
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "0.68rem",
                              textTransform: "uppercase",
                              letterSpacing: 0.35,
                              fontWeight: 700,
                              color: "text.secondary",
                            }}
                          >
                            {item.label}
                          </Typography>
                        </Stack>
                        <Typography
                          sx={{
                            mt: 0.7,
                            fontSize: "0.9rem",
                            fontWeight: 800,
                            color: item.color,
                            lineHeight: 1.2,
                            wordBreak: "break-word",
                          }}
                        >
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Divider />
              </>
            ) : null}

            {(loadingConsolidacion || consolidacion) &&
            pedidoLinksArr.length >= 2 ? (
              <Box sx={{ py: 0.25 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  {t("logisticaPedidos.envioConsolidacionTitulo")}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  {t("logisticaPedidos.envioConsolidacionHint")}
                </Typography>
                {loadingConsolidacion ? (
                  <Typography variant="caption" color="text.secondary">
                    …
                  </Typography>
                ) : consolidacion?.productosConReferencia ? (
                  <>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            {t("logisticaPedidos.envioColPedido")}
                          </TableCell>
                          <TableCell align="right">IdProd</TableCell>
                          <TableCell align="right">
                            {t("logisticaPedidos.envioColCartons")}
                          </TableCell>
                          <TableCell align="right">
                            {t("logisticaPedidos.envioColKg")}
                          </TableCell>
                          <TableCell align="right">
                            {t("logisticaPedidos.envioColCbm")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(consolidacion.porPedido || []).map((row) => {
                          const pl = row.productionLogistics;
                          return (
                            <TableRow key={row.IdPresupuestoA3Pedido}>
                              <TableCell>
                                {row.Referencia ||
                                  `#${row.IdPresupuestoA3Pedido}`}
                              </TableCell>
                              <TableCell align="right">
                                {row.IdProducto ?? "—"}
                              </TableCell>
                              <TableCell align="right">
                                {pl?.NCartons ?? "—"}
                              </TableCell>
                              <TableCell align="right">
                                {pl?.GrossWeightKgs != null
                                  ? fmtQtyMatriz(pl.GrossWeightKgs)
                                  : "—"}
                              </TableCell>
                              <TableCell align="right">
                                {pl?.TotalVolumeCbm != null
                                  ? fmtQtyMatriz(pl.TotalVolumeCbm)
                                  : "—"}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    <Alert severity="info" sx={{ mt: 1.25 }}>
                      {t("logisticaPedidos.envioConsolidacionSuma", {
                        c:
                          consolidacion.sumaNCartons != null
                            ? Math.round(consolidacion.sumaNCartons)
                            : "—",
                        kg:
                          consolidacion.sumaGrossWeightKgs != null
                            ? fmtQtyMatriz(consolidacion.sumaGrossWeightKgs)
                            : "—",
                        cbm:
                          consolidacion.sumaTotalVolumeCbm != null
                            ? fmtQtyMatriz(consolidacion.sumaTotalVolumeCbm)
                            : "—",
                        eta: consolidacion.etaMasTarde || "—",
                      })}
                    </Alert>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mt: 1 }}
                      onClick={aplicarCabeceraDesdeTimeline}
                    >
                      {t("logisticaPedidos.envioConsolidacionAplicar")}
                    </Button>
                  </>
                ) : (
                  <Alert severity="warning">
                    {t("logisticaPedidos.envioConsolidacionSinTimeline")}
                  </Alert>
                )}
              </Box>
            ) : null}

            {error ? (
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            ) : null}
            {!editableMatrix &&
            !multiPedido &&
            (source?.lineas || []).length > 0 ? (
              <Alert severity="warning">
                Las líneas guardadas no comparten la misma pareja de familias
                talla/color: solo se puede editar la cabecera. Para cambiar
                unidades, rehacé la matriz desde producción o contactá soporte.
              </Alert>
            ) : null}
            {effectivePedidoRow && editableMatrix ? (
              <Button
                variant="outlined"
                size="small"
                onClick={() => void loadMatrizFromPedidoA3()}
                disabled={loadingMatriz || !editableMatrix}
                startIcon={<Inventory2OutlinedIcon fontSize="small" />}
                sx={{
                  alignSelf: "flex-start",
                  borderRadius: 999,
                  px: 1.5,
                  backgroundColor: alpha(theme.palette.background.paper, 0.82),
                }}
              >
                {loadingMatriz ? "Copiando…" : "Copiar matriz del pedido (A3)"}
              </Button>
            ) : null}
            <Stack direction="row" spacing={0.9} alignItems="center">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: shipmentMethodMeta.color,
                  backgroundColor: alpha(shipmentMethodMeta.color, 0.12),
                }}
              >
                {shipmentMethodMeta.icon}
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Cabecera logistica
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Campos principales del transporte y documentacion del envio.
                </Typography>
              </Box>
            </Stack>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(3, minmax(0, 1fr))",
                  lg: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr)",
                },
                gap: 1.75,
                alignItems: "start",
              }}
            >
              <HeaderFieldGroup
                id="cabecera-grupo-production"
                title="Producción"
                icon={<FactoryOutlinedIcon />}
                accent={headerGroupAccents.production}
                filled={groupFill.production?.filled}
                total={groupFill.production?.total}
              >
                <CabeceraField
                  sx={softFieldSx}
                  label="Factory"
                  select
                  SelectProps={{ native: true }}
                  value={header.Factory}
                  onChange={(ev) =>
                    setHeader((h) => ({ ...h, Factory: ev.target.value }))
                  }
                >
                  <option value="">—</option>
                  {factorySelectOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </CabeceraField>
                <CabeceraField
                  sx={softFieldSx}
                  label="PO"
                  value={header.POReference}
                  onChange={(ev) =>
                    setHeader((h) => ({ ...h, POReference: ev.target.value }))
                  }
                />
                <CabeceraField
                  sx={softFieldSx}
                  label="PO Date"
                  type="date"
                  value={header.PODate}
                  onChange={(ev) =>
                    setHeader((h) => ({ ...h, PODate: ev.target.value }))
                  }
                />
                <CabeceraField
                  sx={softFieldSx}
                  label="Inspection"
                  type="date"
                  value={header.InspectionDate}
                  onChange={(ev) =>
                    setHeader((h) => ({
                      ...h,
                      InspectionDate: ev.target.value,
                    }))
                  }
                />
                {showFactoryFromRemarks ? (
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    onClick={() =>
                      setHeader((h) => ({ ...h, Factory: remarksFactory }))
                    }
                    label={`Usar: ${remarksFactory}`}
                    sx={{
                      maxWidth: "100%",
                      height: 22,
                      fontSize: "0.62rem",
                      "& .MuiChip-label": {
                        px: 0.7,
                        fontWeight: 700,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                ) : null}
              </HeaderFieldGroup>

              <HeaderFieldGroup
                id="cabecera-grupo-cargo"
                title="Carga"
                icon={<Inventory2OutlinedIcon />}
                accent={headerGroupAccents.cargo}
                filled={groupFill.cargo?.filled}
                total={groupFill.cargo?.total}
              >
                <CabeceraField
                  sx={softFieldSx}
                  label="Cartons"
                  title="Nº cartons"
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  value={header.NCartons}
                  onChange={(ev) =>
                    setHeader((h) => ({ ...h, NCartons: ev.target.value }))
                  }
                />
                <CabeceraField
                  sx={softFieldSx}
                  label="Kg"
                  title="Gross weight (kg)"
                  value={header.GrossWeightKgs}
                  onChange={(ev) =>
                    setHeader((h) => ({
                      ...h,
                      GrossWeightKgs: ev.target.value,
                    }))
                  }
                  inputProps={{ inputMode: "decimal" }}
                />
                <CabeceraField
                  sx={softFieldSx}
                  label="CBM"
                  title="Total volume (CBM)"
                  value={header.TotalVolumeCbm}
                  onChange={(ev) =>
                    setHeader((h) => ({
                      ...h,
                      TotalVolumeCbm: ev.target.value,
                    }))
                  }
                  inputProps={{ inputMode: "decimal" }}
                />
              </HeaderFieldGroup>

              <HeaderFieldGroup
                id="cabecera-grupo-shipment"
                title="Envío"
                icon={<DirectionsBoatOutlinedIcon />}
                accent={headerGroupAccents.shipment}
                filled={groupFill.shipment?.filled}
                total={groupFill.shipment?.total}
              >
                <CabeceraField
                  sx={softFieldSx}
                  label="Method"
                  title="Shipment method"
                  value={header.ShipmentMethod}
                  select
                  SelectProps={{ native: true }}
                  onChange={(ev) =>
                    setHeader((h) => ({
                      ...h,
                      ShipmentMethod: ev.target.value,
                    }))
                  }
                >
                  <option value="">—</option>
                  {SHIPMENT_METHOD_OPTIONS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </CabeceraField>
                <CabeceraField
                  sx={softFieldSx}
                  label="Forwarder"
                  select
                  SelectProps={{ native: true }}
                  value={header.Forwarder}
                  onChange={(ev) =>
                    setHeader((h) => ({ ...h, Forwarder: ev.target.value }))
                  }
                >
                  <option value="">—</option>
                  {forwarderSelectOptions.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </CabeceraField>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1.65,
                  }}
                >
                  <CabeceraField
                    sx={softFieldSx}
                    label="Booking"
                    type="date"
                    value={header.BookingDone}
                    onChange={(ev) =>
                      setHeader((h) => ({ ...h, BookingDone: ev.target.value }))
                    }
                  />
                  <CabeceraField
                    sx={softFieldSx}
                    label="ETD"
                    type="date"
                    value={header.ETD}
                    onChange={(ev) =>
                      setHeader((h) => ({ ...h, ETD: ev.target.value }))
                    }
                  />
                  <CabeceraField
                    sx={softFieldSx}
                    label="ExFD"
                    type="date"
                    value={header.ExFD}
                    onChange={(ev) =>
                      setHeader((h) => ({ ...h, ExFD: ev.target.value }))
                    }
                  />
                  <CabeceraField
                    sx={softFieldSx}
                    label="ETDF"
                    type="date"
                    value={header.ETDF}
                    onChange={(ev) =>
                      setHeader((h) => ({ ...h, ETDF: ev.target.value }))
                    }
                  />
                  <CabeceraField
                    sx={softFieldSx}
                    label="CUT-OFF"
                    type="date"
                    value={header.CutOff}
                    onChange={(ev) =>
                      setHeader((h) => ({ ...h, CutOff: ev.target.value }))
                    }
                  />
                </Box>
              </HeaderFieldGroup>

              <HeaderFieldGroup
                id="cabecera-grupo-tracking"
                title="Tracking"
                icon={<AssignmentOutlinedIcon />}
                accent={headerGroupAccents.tracking}
                filled={groupFill.tracking?.filled}
                total={groupFill.tracking?.total}
              >
                <CabeceraField
                  sx={softFieldSx}
                  label="Code"
                  title="Shipment code (cliente)"
                  value={header.ShipmentCodeCustomer}
                  onChange={(ev) =>
                    setHeader((h) => ({
                      ...h,
                      ShipmentCodeCustomer: ev.target.value,
                    }))
                  }
                />
                <CabeceraField
                  sx={softFieldSx}
                  label="BL / AWB"
                  value={header.BLAWBNr}
                  onChange={(ev) =>
                    setHeader((h) => ({ ...h, BLAWBNr: ev.target.value }))
                  }
                />
                <CabeceraField
                  sx={softFieldSx}
                  label="ETA"
                  type="date"
                  value={header.ETA}
                  onChange={(ev) =>
                    setHeader((h) => ({ ...h, ETA: ev.target.value }))
                  }
                />
                <CabeceraField
                  sx={softFieldSx}
                  label="DD"
                  title="Delivery Date"
                  type="date"
                  value={header.DD}
                  onChange={(ev) =>
                    setHeader((h) => ({ ...h, DD: ev.target.value }))
                  }
                />
              </HeaderFieldGroup>

              <HeaderFieldGroup
                id="cabecera-grupo-documents"
                title="Documentos"
                icon={<DescriptionOutlinedIcon />}
                accent={headerGroupAccents.documents}
                filled={groupFill.documents?.filled}
                total={groupFill.documents?.total}
              >
                <CabeceraField
                  sx={softFieldSx}
                  label="Docs OK"
                  title="Documents approved"
                  type="date"
                  value={header.DocumentsApproved}
                  onChange={(ev) =>
                    setHeader((h) => ({
                      ...h,
                      DocumentsApproved: ev.target.value,
                    }))
                  }
                />
                <CabeceraField
                  sx={softFieldSx}
                  label="Telex"
                  title="Original telex / docs sent"
                  type="date"
                  value={header.OriginalTelexDocsSemt}
                  onChange={(ev) =>
                    setHeader((h) => ({
                      ...h,
                      OriginalTelexDocsSemt: ev.target.value,
                    }))
                  }
                />
                <CabeceraField
                  sx={softFieldSx}
                  label="Link docs"
                  value={header.LinkDocs}
                  onChange={(ev) =>
                    setHeader((h) => ({ ...h, LinkDocs: ev.target.value }))
                  }
                />
                <CabeceraField
                  sx={softFieldSx}
                  label="Remarks"
                  multiline
                  minRows={2}
                  value={header.Remarks}
                  onChange={(ev) =>
                    setHeader((h) => ({ ...h, Remarks: ev.target.value }))
                  }
                />
              </HeaderFieldGroup>
            </Box>

            {editableMatrix ? (
              <Box
                sx={{
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.9),
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, display: "block", mb: 0.5 }}
                >
                  Matriz tallas / colores
                </Typography>
                {normStr(matrixFamiliaH) &&
                normStr(matrixFamiliaV) &&
                normStr(matrixFamiliaH) === normStr(matrixFamiliaV) ? (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    La misma familia en columnas y filas: elegí otra familia en
                    uno de los desplegables.
                  </Alert>
                ) : null}
                {mismatchMatrizVsEstimate ? (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    La suma de la matriz ({fmtQtyMatriz(totalMatriz)}) no
                    coincide con la referencia de unidades del pedido / estimate
                    ({fmtQtyMatriz(unitsCabeceraEstimate)}).
                  </Alert>
                ) : null}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                    gap: 1.25,
                    mb: 1.25,
                  }}
                >
                  <TextField
                    label="Familia de tallas (columnas)"
                    value={matrixFamiliaH}
                    select
                    SelectProps={{ native: true }}
                    disabled={lockFamiliesToArticulo}
                    size="small"
                    onChange={(e) => {
                      setMatrixFamiliaH(e.target.value);
                      setLineasTallas([]);
                    }}
                  >
                    <option value=""></option>
                    {familiesColumnOptions.map((f) => (
                      <option key={`fh-${f.CODFAMTALLA}`} value={f.CODFAMTALLA}>
                        {f.CODFAMTALLA} - {f.DESCFAMTALLA}
                      </option>
                    ))}
                  </TextField>
                  <TextField
                    label="Familia de colores (filas)"
                    value={matrixFamiliaV}
                    select
                    SelectProps={{ native: true }}
                    disabled={lockFamiliesToArticulo}
                    size="small"
                    onChange={(e) => {
                      setMatrixFamiliaV(e.target.value);
                      setLineasTallas([]);
                    }}
                  >
                    <option value=""></option>
                    {familiesRowOptions.map((f) => (
                      <option key={`fv-${f.CODFAMTALLA}`} value={f.CODFAMTALLA}>
                        {f.CODFAMTALLA} - {f.DESCFAMTALLA}
                      </option>
                    ))}
                  </TextField>
                </Box>
                {lockFamiliesToArticulo ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.75 }}
                  >
                    Familias según artículo A3: columnas{" "}
                    <strong>{resolvedFamilyH}</strong>, filas{" "}
                    <strong>{resolvedFamilyV}</strong>.
                  </Typography>
                ) : null}
                {!matrixFamiliaH || !matrixFamiliaV ? (
                  <Typography variant="caption" color="text.secondary">
                    Elegí las dos familias.
                  </Typography>
                ) : tallasH.length === 0 || tallasV.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No hay tallas en catálogo para esas familias (o aún se están
                    cargando).
                  </Typography>
                ) : (
                  <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
                    <Table size="small" sx={{ minWidth: 520 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 0,
                              bgcolor: "background.paper",
                              zIndex: 1,
                              fontWeight: 700,
                            }}
                          >
                            Lateral / Superior
                          </TableCell>
                          {tallasH.map((h) => (
                            <TableCell
                              key={`ch-${String(h.CODTALLA)}`}
                              align="right"
                            >
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 600 }}
                              >
                                {tallaLabel(h)}
                              </Typography>
                            </TableCell>
                          ))}
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            Σ fila (uds)
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tallasV.map((v) => {
                          const rowSum = tallasH.reduce(
                            (s, h) => s + qtyForCell(v, h),
                            0,
                          );
                          return (
                            <TableRow key={`vr-${String(v.CODTALLA)}`}>
                              <TableCell
                                sx={{
                                  position: "sticky",
                                  left: 0,
                                  bgcolor: "background.paper",
                                  zIndex: 1,
                                  whiteSpace: "nowrap",
                                  fontWeight: 600,
                                }}
                              >
                                {colorLabel(v)}
                              </TableCell>
                              {tallasH.map((h) => {
                                const q = qtyForCell(v, h);
                                return (
                                  <TableCell
                                    key={`c-${String(v.CODTALLA)}-${String(h.CODTALLA)}`}
                                    align="right"
                                    sx={{ py: 0.5, minWidth: 72 }}
                                  >
                                    <TextField
                                      size="small"
                                      value={
                                        hasMatrixCell(v, h) ? String(q) : ""
                                      }
                                      placeholder="0"
                                      inputProps={{
                                        inputMode: "decimal",
                                        style: {
                                          textAlign: "right",
                                          padding: "4px 6px",
                                        },
                                      }}
                                      onChange={(e) =>
                                        updateMatrixCell(v, h, e.target.value)
                                      }
                                    />
                                  </TableCell>
                                );
                              })}
                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                {fmtQtyMatriz(rowSum)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>
                            Σ columna (uds)
                          </TableCell>
                          {colTotals.map((ct, i) => (
                            <TableCell
                              key={`ft-${i}`}
                              align="right"
                              sx={{ fontWeight: 600 }}
                            >
                              {fmtQtyMatriz(ct)}
                            </TableCell>
                          ))}
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {fmtQtyMatriz(totalMatriz)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  Total matriz: <strong>{fmtQtyMatriz(totalMatriz)} uds</strong>
                </Typography>
              </Box>
            ) : null}
            {pedidoLinksArr.length >= 2 ? (
              <>
                <Divider sx={{ my: 2 }} />
                <LogisticaPedidosMatricesReferencia
                  pedidoLinks={pedidoLinksArr}
                  fallbackDb={a3DbSource}
                  envioLineas={
                    Array.isArray(source?.lineas) ? source.lineas : []
                  }
                  seedPedidoId={seedPedidoId}
                  showEnvioConsolidada={false}
                  pedidoHeaders={pedidoHeaders}
                  matrixEditable={multiPedidoMatrixEditable}
                  onPedidoLineasChange={(idPed, lineas) => {
                    const pedidoId = Number(idPed);
                    const nextLineas = Array.isArray(lineas) ? lineas : [];
                    const totalPedidoUnits =
                      totalUnitsFromEnvioLineas(nextLineas);
                    setPedidoLineasByPedido((prev) => ({
                      ...prev,
                      [pedidoId]: nextLineas,
                    }));
                    setPedidoHeaders((prev) => ({
                      ...prev,
                      [pedidoId]: {
                        ...(prev[pedidoId] || {
                          IdPresupuestoA3Pedido: pedidoId,
                        }),
                        Unidades:
                          totalPedidoUnits > 0 ? String(totalPedidoUnits) : "",
                      },
                    }));
                  }}
                  onHeaderChange={(idPed, field, value) =>
                    setPedidoHeaders((prev) => ({
                      ...prev,
                      [idPed]: {
                        IdPresupuestoA3Pedido: Number(idPed),
                        ...(prev[idPed] || {}),
                        [field]: value,
                      },
                    }))
                  }
                  headerEditable
                  removablePedidos={multiPedido}
                  removingPedidoId={unlinkingPedidoId}
                  onRemovePedido={requestRemovePedido}
                />
              </>
            ) : null}
            {idEnvio ? (
              <>
                <Divider sx={{ my: 2 }} />
                <DocumentosLogistica logisticaEnvioId={idEnvio} />
              </>
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: alpha(theme.palette.primary.main, 0.12),
          backgroundColor: alpha(theme.palette.background.paper, 0.92),
        }}
      >
        <Button
          onClick={onClose}
          disabled={saving || loadingDetail}
          sx={{ borderRadius: 999, px: 1.8 }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSave()}
          disabled={saving || loadingDetail}
          sx={{
            borderRadius: 999,
            px: 2.1,
            boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, 0.24)}`,
          }}
        >
          {saving ? "Guardando…" : "Guardar cambios"}
        </Button>
      </DialogActions>
      <Dialog
        open={Boolean(removePedidoDialog)}
        onClose={() => {
          if (!unlinkingPedidoId) setRemovePedidoDialog(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Quitar pedido del envío</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se quitará {removePedidoDialog?.label ?? "este pedido"} del envío.
            Los cambios no guardados de este diálogo se perderán.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setRemovePedidoDialog(null)}
            disabled={Boolean(unlinkingPedidoId)}
          >
            Cancelar
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => void handleRemovePedido()}
            disabled={Boolean(unlinkingPedidoId)}
          >
            {unlinkingPedidoId ? "Quitando..." : "Quitar del envío"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default LogisticaEnvioEditDialog;
